import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { ReporterData, RoleEnum, SignalingOpportunityDTO, SignalingSearchParams } from '../../api';
import _ from 'lodash';
import Restricted from '../../components/Restricted/Restricted';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
// @ts-ignore
import './ReportListPage.scss';
import i18next from 'i18next';
import Btn from 'src/components/Btn/Btn';
import { FaPlus } from 'react-icons/fa';
import { useCallback, useEffect, useState } from 'react';
import TableSkeleton from 'src/components/TableSkeleton/TableSkeleton';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { useApiClient } from 'src/hooks/useApiClient';
import { CreateNewReportModal } from 'src/components/Reports/CreateNewReportModal';
import { ReportDetailOverlay } from 'src/components/Reports/ReportDetailOverlay';
import * as Utils from 'src/components/Reporter/reporterUtils';
import { formatDate } from 'src/utils';
import { PeriodOpportunitySection, StatsBar } from 'src/components/Reporter';

type FilterQuery = Partial<{
  skip: number;
  limit: number;
}> &
  Partial<{
    orderBy: string;
    orderDate?: string;
    ascending: boolean;
  }> &
  Partial<{
    q: string;
  }>

const DEFAULT_PAGINATION: { skip: number; limit: number } = {
  skip: 0,
  limit: 5000,
} as const;

const INIT_FILTER_QUERY: SignalingSearchParams = {
  q: '',
  ...DEFAULT_PAGINATION
} as const;

export const ReportListPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState<any>(null);
  const { showError } = useErrorToast();
  const [filterQuery, setFilterQuery] = useState<FilterQuery>(
    Object.assign({}, INIT_FILTER_QUERY)
  );
  const [tableIsBusy, setTableIsBusy] = useState<boolean>(false);
  const { apiClient, handleApi } = useApiClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<SignalingOpportunityDTO | null>(null);
  const [dateRange] = useState(Utils.getSlidingDateRange(6, 0));
  const [overviewData, setOverviewData] = useState<ReporterData>({
    stats: {
      opportunityCreated: 0,
      opportunityWon: 0,
      provvigioniSegnalatore: 0,
      provvigioniDealerVinte: 0,
    },
    periods: []
  })

  const statsPeriod = {
    start: formatDate(dateRange.createdDateFrom || '', 'it-IT', false),
    end: formatDate(dateRange.createdDateTo || '', 'it-IT', false),
  };

  useEffect(() => {
    setError(null);
    handleApi(
      apiClient.salesforceOpportunityController.getMyReports({
        ...DEFAULT_PAGINATION,
        ...dateRange,
      })
    ).then((res) => {
      setResponse(res);
      setOverviewData({
        stats: Utils.computeStats(res.data),
        periods: Utils.groupByMonthYearDesc(res.data)
      })
    })
      .catch((err) => {
        setError(err);
        showError(err, i18next.t('error_loading_data'));
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const computeQueryParams = useCallback((queryParams) => {
    return _.omitBy(
      queryParams,
      (value: any) => _.isNil(value) || (_.isString(value) && _.isEmpty(value))
    );
  }, []);

  const refreshTable = useCallback(
    async (queryParams: FilterQuery, opts = { ...DEFAULT_PAGINATION }) => {
      setTableIsBusy(true);
      setLoading(true)
      handleApi(
        apiClient.salesforceOpportunityController.getMyReports({
          ...(computeQueryParams(queryParams) || {}),
          ...opts,
          ...dateRange,
        })
      )
        .then((res) => {
          setResponse(res);
          setOverviewData({
            stats: Utils.computeStats(res.data),
            periods: Utils.groupByMonthYearDesc(res.data)
          })
          setError(null);
        })
        .catch((err) => {
          showError(err, i18next.t('fetch_my_reports_error'));
        })
        .finally(() => {
          setLoading(false)
          setTableIsBusy(false);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [loading, showError, tableIsBusy]
  );


  const handleResetFilter = useCallback(() => {
    const filter = Object.assign({}, INIT_FILTER_QUERY);
    setFilterQuery(filter);
    refreshTable(filter);
  }, [refreshTable]);

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col md={6} className="display-6">
          {i18next.t('my_opportunities_list')}
        </Col>
        {/* <Col md={6} className="mt-4 mt-md-0 text-md-end">
          <Btn
            disabled={loading}
            onClick={() => setIsCreateModalOpen(true)}
          >
            <FaPlus />
            {i18next.t('create_opportunity')}
          </Btn>
        </Col> */}
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  refreshTable(filterQuery);
                }}
              >
                <Row className="align-items-end">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Control
                        onChange={({ target }) => {
                          setFilterQuery({
                            ...filterQuery,
                            q: target.value,
                          });
                        }}
                        placeholder={i18next.t('search')}
                        type="search"
                        value={filterQuery.q}
                        name="q"
                      />
                    </Form.Group>
                  </Col>
                  <Col className="text-end">
                    <Button
                      disabled={tableIsBusy}
                      variant="secondary"
                      onClick={handleResetFilter}
                      className="me-2"
                    >
                      {i18next.t('reset')}
                    </Button>
                    <Button disabled={tableIsBusy} type="submit">
                      {tableIsBusy ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        i18next.t('search')
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Restricted to={[RoleEnum.SEGNALATORE, RoleEnum.MASTER]}>
        <ErrorBoundary>
          {!loading && response ? (
            <div>
              <StatsBar
                className="mt-3 mb-3"
                stats={overviewData.stats}
                period={statsPeriod}
                labelProvvigioniSegnalatore={i18next.t('provvigioni_maturate')}
              />
              {overviewData.periods.length > 0
                ? (<div className="d-flex flex-column gap-4">
                  {overviewData.periods.map((p, idx) => (
                    <PeriodOpportunitySection {...p} key={idx} />
                  ))}
                </div>)
                : (<div className="mt-2">
                  <Alert>{i18next.t('empty_state_message')}</Alert>
                </div>)}
            </div>
          ) : (
            <TableSkeleton />
          )}
        </ErrorBoundary>
      </Restricted>
      <CreateNewReportModal
        isOpen={isCreateModalOpen}
        closeModal={(reload?: boolean) => {
          setIsCreateModalOpen(false);
          reload && handleResetFilter();
        }}
      />
      <ReportDetailOverlay
        open={!!selectedOpportunity && isDetailOpen}
        onOpenChange={(bool) => {
          setIsDetailOpen(bool);
          if (!bool) {
            setSelectedOpportunity(null);
          }
        }}
        opportunity={selectedOpportunity}
      />
    </Container>
  )
}

